import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import { doc, getDoc, setDoc, collection, getDocs, query, where } from 'firebase/firestore';
import {
  ArrowLeftIcon,
  AcademicCapIcon,
  ClipboardDocumentCheckIcon,
  UserGroupIcon,
  ChartBarIcon,
  SparklesIcon,
  DocumentTextIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import type { Curriculum, CurriculumCourse, CurriculumLevel } from '../types';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export default function CurriculumCourseDetail() {
  const { curriculumId, courseId } = useParams();
  const { currentUser } = useAuth();
  const [curriculum, setCurriculum] = useState<Curriculum | null>(null);
  const [course, setCourse] = useState<CurriculumCourse | null>(null);
  const [loading, setLoading] = useState(true);
  const [outcomes, setOutcomes] = useState<string[]>([]);
  const [newOutcome, setNewOutcome] = useState('');
  const [contentToExtract, setContentToExtract] = useState('');
  const [aiLoading, setAiLoading] = useState<'suggest' | 'extract' | null>(null);
  const [aiError, setAiError] = useState('');
  const [suggestedOutcomes, setSuggestedOutcomes] = useState<string[] | null>(null);
  const [extractedOutcomes, setExtractedOutcomes] = useState<string[] | null>(null);
  const [levels, setLevels] = useState<CurriculumLevel[]>([]);
  const [otherCourses, setOtherCourses] = useState<CurriculumCourse[]>([]);

  useEffect(() => {
    if (!currentUser || !curriculumId || !courseId) return;
    const fetch = async () => {
      const [curSnap, courseSnap, levelsSnap, coursesSnap] = await Promise.all([
        getDoc(doc(db, 'curricula', curriculumId)),
        getDoc(doc(db, 'curriculumCourses', courseId)),
        getDocs(query(collection(db, 'curriculumLevels'), where('curriculumId', '==', curriculumId), where('uid', '==', currentUser.uid))),
        getDocs(query(collection(db, 'curriculumCourses'), where('curriculumId', '==', curriculumId), where('uid', '==', currentUser.uid))),
      ]);
      if (curSnap.exists() && curSnap.data().uid === currentUser.uid) {
        setCurriculum(curSnap.data() as Curriculum);
      }
      if (
        courseSnap.exists() &&
        courseSnap.data().uid === currentUser.uid &&
        courseSnap.data().curriculumId === curriculumId
      ) {
        const data = courseSnap.data() as CurriculumCourse;
        setCourse(data);
        setOutcomes(data.outcomes || []);
      }
      setLevels(levelsSnap.docs.map(d => d.data() as CurriculumLevel).sort((a, b) => a.order - b.order));
      const allCourses = coursesSnap.docs.map(d => d.data() as CurriculumCourse).sort((a, b) => a.order - b.order);
      setOtherCourses(allCourses.filter(c => c.id !== courseId));
      setLoading(false);
    };
    fetch();
  }, [currentUser, curriculumId, courseId]);

  const saveOutcomes = async (updated: string[]) => {
    if (!courseId || !course) return;
    const next = { ...course, outcomes: updated };
    await setDoc(doc(db, 'curriculumCourses', courseId), next);
    setCourse(next);
    setOutcomes(updated);
  };

  const saveCourseMeta = async (updates: { levelId?: string; prerequisiteCourseIds?: string[] }) => {
    if (!courseId || !course) return;
    const next = { ...course, ...updates };
    await setDoc(doc(db, 'curriculumCourses', courseId), next);
    setCourse(next);
  };

  const handleAddOutcome = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = newOutcome.trim();
    if (!trimmed) return;
    saveOutcomes([...outcomes, trimmed]);
    setNewOutcome('');
  };

  const handleRemoveOutcome = (index: number) => {
    saveOutcomes(outcomes.filter((_, i) => i !== index));
  };

  const handleSuggestOutcomes = async () => {
    if (!course) return;
    setAiError('');
    setAiLoading('suggest');
    setSuggestedOutcomes(null);
    try {
      const res = await fetch(`${API_BASE}/api/ai/suggest-outcomes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: course.title, description: course.description || '' }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Kazanım önerileri alınamadı.');
      setSuggestedOutcomes(data.outcomes || []);
    } catch (e: any) {
      setAiError(e.message || 'Bir hata oluştu.');
    } finally {
      setAiLoading(null);
    }
  };

  const handleExtractOutcomes = async () => {
    if (!contentToExtract.trim()) {
      setAiError('Lütfen içerik metnini girin.');
      return;
    }
    setAiError('');
    setAiLoading('extract');
    setExtractedOutcomes(null);
    try {
      const res = await fetch(`${API_BASE}/api/ai/extract-outcomes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: contentToExtract.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Kazanım çıkarılamadı.');
      setExtractedOutcomes(data.outcomes || []);
    } catch (e: any) {
      setAiError(e.message || 'Bir hata oluştu.');
    } finally {
      setAiLoading(null);
    }
  };

  const addSuggestedOutcomes = (toAdd: string[]) => {
    const merged = [...outcomes];
    toAdd.forEach(o => {
      if (o.trim() && !merged.includes(o.trim())) merged.push(o.trim());
    });
    saveOutcomes(merged);
    setSuggestedOutcomes(null);
  };

  const addExtractedOutcomes = (toAdd: string[]) => {
    const merged = [...outcomes];
    toAdd.forEach(o => {
      if (o.trim() && !merged.includes(o.trim())) merged.push(o.trim());
    });
    saveOutcomes(merged);
    setExtractedOutcomes(null);
    setContentToExtract('');
  };

  if (!currentUser) return <div className="p-8">Yükleniyor...</div>;
  if (loading) return <div className="card animate-pulse h-48" />;
  if (!course || !curriculum) return <div className="p-8 text-gray-500">Kurs bulunamadı.</div>;

  return (
    <div className="space-y-6">
      <Link
        to={`/app/curricula/${curriculumId}`}
        className="inline-flex items-center text-sm text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400"
      >
        <ArrowLeftIcon className="w-4 h-4 mr-1" />
        {curriculum.title} müfredatına dön
      </Link>

      <div className="card">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{course.title}</h1>
        {course.description && (
          <p className="mt-2 text-gray-600 dark:text-gray-400">{course.description}</p>
        )}
        {course.duration && (
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-500">Süre: {course.duration} dakika</p>
        )}
      </div>

      {/* Seviye ve Ön Koşullar */}
      <div className="card border-l-4 border-blue-500">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
            <AcademicCapIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white">Seviye & Ön Koşullar</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Müfredat görünümünde bu kursun konumunu belirler
            </p>
          </div>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Seviye
            </label>
            <select
              value={course.levelId || ''}
              onChange={e => saveCourseMeta({ levelId: e.target.value || undefined })}
              className="input w-full max-w-xs"
            >
              <option value="">Seçin (atama yok)</option>
              {levels.map(l => (
                <option key={l.id} value={l.id}>
                  {l.name}
                </option>
              ))}
            </select>
            {levels.length === 0 && (
              <p className="mt-1 text-xs text-gray-500">
                Seviye eklemek için müfredat sayfasından "Seviye Ekle" kullanın.
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Ön koşul kurslar (bu kursu almadan önce tamamlanması gerekenler)
            </label>
            {otherCourses.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">Bu müfredatta başka kurs yok.</p>
            ) : (
              <div className="flex flex-wrap gap-3">
                {otherCourses.map(c => {
                  const checked = (course.prerequisiteCourseIds || []).includes(c.id);
                  return (
                    <label
                      key={c.id}
                      className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                        checked
                          ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                          : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={e => {
                          const ids = course.prerequisiteCourseIds || [];
                          const next = e.target.checked
                            ? [...ids, c.id]
                            : ids.filter(id => id !== c.id);
                          saveCourseMeta({ prerequisiteCourseIds: next });
                        }}
                        className="rounded"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300 truncate max-w-[200px]" title={c.title}>
                        {c.title}
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Kazanımlar (Learning Outcomes) */}
      <div className="card border-l-4 border-emerald-500">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 rounded-lg bg-emerald-100 dark:bg-emerald-900/30">
            <ClipboardDocumentCheckIcon className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white">Öğrenme Kazanımları</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">Kurs bitiminde öğrencinin yapabilecekleri</p>
          </div>
        </div>
        {aiError && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm">
            {aiError}
          </div>
        )}
        <ul className="space-y-2 mb-4">
          {outcomes.length === 0 ? (
            <li className="text-gray-500 dark:text-gray-400 text-sm">Henüz kazanım eklenmedi.</li>
          ) : (
            outcomes.map((o, i) => (
              <li key={i} className="flex items-start gap-2 group">
                <span className="flex-1 text-gray-700 dark:text-gray-300">{o}</span>
                <button
                  type="button"
                  onClick={() => handleRemoveOutcome(i)}
                  className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-600 p-1"
                  aria-label="Kaldır"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              </li>
            ))
          )}
        </ul>
        <form onSubmit={handleAddOutcome} className="flex gap-2 mb-6">
          <input
            type="text"
            value={newOutcome}
            onChange={e => setNewOutcome(e.target.value)}
            placeholder="Yeni kazanım yazın..."
            className="input flex-1"
          />
          <button type="submit" className="btn-primary inline-flex items-center">
            <PlusIcon className="w-4 h-4 mr-1" /> Ekle
          </button>
        </form>
        <div className="flex flex-wrap gap-2 mb-4">
          <button
            type="button"
            onClick={handleSuggestOutcomes}
            disabled={aiLoading !== null}
            className="inline-flex items-center px-3 py-2 rounded-lg bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 hover:bg-primary-200 dark:hover:bg-primary-800/50 text-sm font-medium disabled:opacity-50"
          >
            <SparklesIcon className="w-4 h-4 mr-1.5" />
            {aiLoading === 'suggest' ? 'Öneriliyor...' : 'AI ile kazanım öner'}
          </button>
        </div>
        {suggestedOutcomes && suggestedOutcomes.length > 0 && (
          <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-800/50 space-y-2">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Önerilen kazanımlar — istediklerinizi ekleyin:</p>
            <ul className="space-y-1">
              {suggestedOutcomes.map((o, i) => (
                <li key={i} className="flex items-center gap-2">
                  <span className="flex-1 text-sm text-gray-600 dark:text-gray-400">{o}</span>
                  <button
                    type="button"
                    onClick={() => addSuggestedOutcomes([o])}
                    className="text-xs text-primary-600 dark:text-primary-400 hover:underline"
                  >
                    Ekle
                  </button>
                </li>
              ))}
            </ul>
            <button
              type="button"
              onClick={() => addSuggestedOutcomes(suggestedOutcomes)}
              className="mt-2 text-sm text-primary-600 dark:text-primary-400 hover:underline"
            >
              Hepsini ekle
            </button>
          </div>
        )}
        <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">İçerikten kazanım çıkar</p>
          <textarea
            value={contentToExtract}
            onChange={e => setContentToExtract(e.target.value)}
            placeholder="Ders notu, ünite metni veya içerik yapıştırın..."
            className="input w-full min-h-[100px] mb-2"
            rows={4}
          />
          <button
            type="button"
            onClick={handleExtractOutcomes}
            disabled={aiLoading !== null}
            className="inline-flex items-center px-3 py-2 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-200 dark:hover:bg-emerald-800/50 text-sm font-medium disabled:opacity-50"
          >
            <DocumentTextIcon className="w-4 h-4 mr-1.5" />
            {aiLoading === 'extract' ? 'Çıkarılıyor...' : 'İçerikten kazanım çıkar'}
          </button>
        </div>
        {extractedOutcomes && extractedOutcomes.length > 0 && (
          <div className="mt-4 p-4 rounded-lg bg-gray-50 dark:bg-gray-800/50 space-y-2">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Çıkarılan kazanımlar:</p>
            <ul className="space-y-1">
              {extractedOutcomes.map((o, i) => (
                <li key={i} className="flex items-center gap-2">
                  <span className="flex-1 text-sm text-gray-600 dark:text-gray-400">{o}</span>
                  <button
                    type="button"
                    onClick={() => addExtractedOutcomes([o])}
                    className="text-xs text-primary-600 dark:text-primary-400 hover:underline"
                  >
                    Ekle
                  </button>
                </li>
              ))}
            </ul>
            <button
              type="button"
              onClick={() => addExtractedOutcomes(extractedOutcomes)}
              className="mt-2 text-sm text-primary-600 dark:text-primary-400 hover:underline"
            >
              Hepsini ekle
            </button>
          </div>
        )}
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <div className="card border-l-4 border-primary-500">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary-100 dark:bg-primary-900/30">
              <AcademicCapIcon className="w-6 h-6 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white">İçerik & Dersler</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">Modüller, videolar, materyaller</p>
            </div>
          </div>
          <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">Yakında eklenecek</p>
        </div>

        <div className="card border-l-4 border-green-500 opacity-90">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/30">
              <ClipboardDocumentCheckIcon className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white">Ölçme & Değerlendirme</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">Quiz, sınav, ödevler</p>
            </div>
          </div>
          <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">Yakında eklenecek</p>
        </div>

        <div className="card border-l-4 border-amber-500 opacity-90">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/30">
              <UserGroupIcon className="w-6 h-6 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white">Atamalar</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">Doğru kişiye atama</p>
            </div>
          </div>
          <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">Yakında eklenecek</p>
        </div>

        <div className="card border-l-4 border-purple-500 opacity-90 md:col-span-2">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/30">
              <ChartBarIcon className="w-6 h-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white">ROI & Raporlama</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Eğitim yatırımının getirisi, tamamlama oranları
              </p>
            </div>
          </div>
          <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">Yakında eklenecek</p>
        </div>
      </div>
    </div>
  );
}
